import {
  type messageCallback,
  type InboundMessage,
  type MessageFilter,
} from 'ably';
import { Realtime } from 'ably';
import { useCallback, useEffect } from 'react';

import { type FlagNames } from 'hb-react/shared/hooks/useFeatureFlag';
import { useFeatureFlagAsync } from 'hb-react/shared/hooks/useFeatureFlag';
import { useAblyAuthUrl } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/hooks/useAblyAuthUrl';
import { useAblyTokenType } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/hooks/useAblyTokenType';
import { type AblyTokenType } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/types';
import { getInactivityPublisherSingleton } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/utils/ablyInactivityPublisherSingleton';
import { clusterToEnv } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/utils/clusterToEnv';
import { SimpleMutex } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/utils/SimpleMutex';
import { InactivityPublisher } from 'hb-react/shared/utils/InactivityPublisher';

interface MessageHandlerRegistration {
  callback: messageCallback<InboundMessage>;
  channel: string;
  filter?: Array<string> | MessageFilter | string;
  id: string;
  onResubscribedAfterInactivity?: (() => void) | null;
  onUnsubscribedDueToInactivity?: () => void;
}

let realtime: Realtime | undefined;
let activeTokenType: AblyTokenType | undefined;
const realtimeMutex = new SimpleMutex();
const messageHandlers = new Map<string, MessageHandlerRegistration>();
// Initialize with a no-op tracker (always active) by default
let inactivityTracker: InactivityPublisher = new InactivityPublisher([]);
let trackerInitializationPromise: Promise<void> | null = null;

// DO NOT USE OUTSIDE OF TESTING
export const testOnlyResetLocalState = () => {
  realtime = undefined;
  activeTokenType = undefined;
  messageHandlers.clear();
  inactivityTracker = new InactivityPublisher([]);
  trackerInitializationPromise = null;
  realtimeMutex.cleanup();
};

const createOrConnectAblyRealtime = async (
  authUrl: string,
  flagsPromise: Promise<Record<FlagNames, boolean>>,
): Promise<Realtime> => {
  const { flagUjAblyClientMigration } = await flagsPromise;

  if (!realtime) {
    // Using a global variable to store the Ably client for now to ensure we share a single
    // connection across the app even when multiple components using portals are mounted.
    if (flagUjAblyClientMigration) {
      realtime = new Realtime({
        authUrl,
        endpoint: window.__HB_ENV__.ABLY_FRONTEND_CONFIG.ENDPOINT ?? undefined,
        fallbackHosts:
          window.__HB_ENV__.ABLY_FRONTEND_CONFIG.FALLBACK_HOSTS ?? undefined,
        logLevel: 1,
        autoConnect: false,
      });
    } else {
      realtime = new Realtime({
        authUrl,
        environment: clusterToEnv(window.__HB_ENV__.CLUSTER_NAME),
        logLevel: 1,
        autoConnect: false,
      });
    }
  }

  // See https://ably.com/docs/connect/states
  switch (realtime.connection.state) {
    case 'connected':
      break;
    case 'initialized':
    case 'suspended':
    case 'closed':
    case 'failed':
      realtime.connection.connect();
      await realtime.connection.once('connected');
      break;
    // From https://ably.com/docs/connect/states: In the disconnected state an SDK will periodically attempt to open
    // a new connection (approximately every 15 seconds), anticipating the connection will be re-established soon
    // and connection and channel continuity will be possible.
    case 'disconnected':
    case 'connecting':
      await realtime.connection.once('connected');
      break;
    case 'closing':
      await realtime.connection.once('closed');
      realtime.connection.connect();
      await realtime.connection.once('connected');
      break;
    default:
      throw new Error('Unknown connection state');
  }

  return realtime;
};

const subscribeHandlerToChannel = async (
  registration: MessageHandlerRegistration,
): Promise<void> => {
  if (!realtime) {
    throw new Error('Realtime not initialized');
  }

  const channel = realtime.channels.get(registration.channel);

  if (registration.filter === undefined) {
    await channel.subscribe(registration.callback);
  } else if (typeof registration.filter === 'string') {
    await channel.subscribe(registration.filter, registration.callback);
  } else if (Array.isArray(registration.filter)) {
    await channel.subscribe(registration.filter, registration.callback);
  } else {
    await channel.subscribe(registration.filter, registration.callback);
  }
};

const unsubscribeHandlerFromChannel = async (
  registration: MessageHandlerRegistration,
): Promise<void> => {
  if (!realtime) {
    return;
  }

  const channel = realtime.channels.get(registration.channel);

  channel.unsubscribe(registration.callback);

  // @ts-expect-error Ably types are not up-to-date
  if (channel.subscriptions.any.length === 0) {
    const channelState = channel.state;
    if (channelState === 'attached' || channelState === 'attaching') {
      try {
        if (channelState === 'attaching') {
          await channel.once('attached');
        }
        await channel.detach();
      } catch {
        // Given the asynchronous nature of detaching the channel, it is possible
        // for race conditions to happen and detach to throw an error (e.g., if
        // a detach is superseded by an attach, or the channel is already detached).
        // We can safely ignore the error as the channel will be in the desired state.
      }
    }
  }
};

const closeRealtimeIfUnused = (): void => {
  if (!realtime || messageHandlers.size > 0) {
    return;
  }

  if (realtime.connection.state === 'connected') {
    try {
      realtime.close();
    } catch {
      // Given the asynchronous nature of closing the connection, it is possible
      // for race conditions to happen and close to throw an error.
      // We can safely ignore the error.
    }
  }
};

const validateTokenType = (tokenType: AblyTokenType): void => {
  // It`s not possible to support multiple token types on the same page.
  // Fail fast to ensure developers are aware of this limitation.
  if (activeTokenType && activeTokenType !== tokenType) {
    throw new Error(
      `Ably tokenType mismatch. Realtime has been created with: ${activeTokenType} but you are trying to use ${tokenType}.`,
    );
  }
};

interface UseAblyRealtimeProps {
  tokenType?: AblyTokenType;
}

export interface UseAblyRealtimeReturn {
  isAblyEnabled: boolean;
  registerMessageHandler: (
    registration: Omit<MessageHandlerRegistration, 'id'> & { id: string },
  ) => Promise<void>;
  unregisterMessageHandler: (id: string) => Promise<void>;
}

export const useAblyRealtime = ({
  tokenType: tokenTypeOverride,
}: UseAblyRealtimeProps = {}): UseAblyRealtimeReturn => {
  const flagsPromise = useFeatureFlagAsync();
  const tokenType = useAblyTokenType({ tokenTypeOverride });
  const authUrl = useAblyAuthUrl(tokenType);
  const isAblyEnabled = !!authUrl;

  validateTokenType(tokenType);

  const registerMessageHandler = useCallback(
    async (
      registration: Omit<MessageHandlerRegistration, 'id'> & { id: string },
    ): Promise<void> => {
      if (!isAblyEnabled) {
        throw new Error('Ably is not enabled');
      }

      await realtimeMutex.runExclusive(async () => {
        validateTokenType(tokenType);

        if (!activeTokenType) {
          activeTokenType = tokenType;
        }

        messageHandlers.set(registration.id, registration);

        if (inactivityTracker.isActive) {
          await createOrConnectAblyRealtime(authUrl, flagsPromise);
          await subscribeHandlerToChannel(registration);
        }
      });
    },
    [authUrl, flagsPromise, isAblyEnabled, tokenType],
  );

  const unregisterMessageHandler = useCallback(
    async (id: string): Promise<void> => {
      await realtimeMutex.runExclusive(async () => {
        const registration = messageHandlers.get(id);
        if (!registration) {
          closeRealtimeIfUnused();

          return;
        }

        messageHandlers.delete(id);

        if (inactivityTracker.isActive) {
          await unsubscribeHandlerFromChannel(registration);
        }

        closeRealtimeIfUnused();
      });
    },
    [],
  );

  const handleInactive = useCallback(async () => {
    await realtimeMutex.runExclusive(async () => {
      // Unsubscribe and detach all channels, waiting for each to complete
      const unsubscribePromises = Array.from(messageHandlers.values()).map(
        async (registration) => {
          await unsubscribeHandlerFromChannel(registration);
          registration.onUnsubscribedDueToInactivity?.();
        },
      );

      await Promise.allSettled(unsubscribePromises);

      closeRealtimeIfUnused();
    });
  }, []);

  const handleActive = useCallback(async () => {
    if (messageHandlers.size === 0 || !authUrl) {
      return;
    }

    await realtimeMutex.runExclusive(async () => {
      if (messageHandlers.size === 0 || !authUrl) {
        return;
      }

      await createOrConnectAblyRealtime(authUrl, flagsPromise);

      const currentRealtime = realtime;
      if (currentRealtime) {
        const channelsToClean = new Set<string>();
        messageHandlers.forEach((registration) => {
          channelsToClean.add(registration.channel);
        });

        await Promise.allSettled(
          Array.from(channelsToClean).map(async (channelName) => {
            const channel = currentRealtime.channels.get(channelName);
            if (channel.state === 'detaching') {
              try {
                await channel.once('detached');
              } catch {
                // Channel might already be detached or in another state
              }
            }
          }),
        );
      }

      const handlers = Array.from(messageHandlers.values());
      for (const registration of handlers) {
        await subscribeHandlerToChannel(registration);
        registration.onResubscribedAfterInactivity?.();
      }
    });
  }, [authUrl, flagsPromise]);

  // Set up inactivity tracker (only once globally)
  useEffect(() => {
    flagsPromise.then(({ flagEvAblyInactivityTracker }) => {
      if (!isAblyEnabled || !flagEvAblyInactivityTracker) {
        return;
      }

      // Atomic initialization pattern: ensure only one initialization happens
      if (!trackerInitializationPromise) {
        trackerInitializationPromise = Promise.resolve().then(() => {
          const tracker = getInactivityPublisherSingleton();
          inactivityTracker = tracker;

          tracker.subscribeToOnActive(handleActive);
          tracker.subscribeToOnInactive(handleInactive);
        });
      }
    });
  }, [flagsPromise, handleActive, handleInactive, isAblyEnabled]);

  return { registerMessageHandler, unregisterMessageHandler, isAblyEnabled };
};
